import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Button,
  Skeleton,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  TREATMENT_RATE_LABELS as LABELS,
  TREATMENT_RATE_LABEL_DESCRIPTIONS as LABEL_DESCRIPTIONS,
  TREATMENT_RATE_WIDGET_TEXT,
} from "./treatmentRateTextConstants";

const COLORS = {
  treatmentRate: "#3b6d11",
  percentagePending: "#c0dd97",
  timeToTherapy: "#185fa5",
};

const INFO_TOOLTIP_SLOT_PROPS = {
  popper: {
    modifiers: [{ name: "offset", options: { offset: [0, -10] } }],
  },
  tooltip: {
    sx: {
      bgcolor: "#FFFFFF",
      color: "#2D2D2D",
      border: "1px solid #E6E6E6",
      boxShadow: "0px 1px 2px 0px #0000000D",
      maxWidth: 260,
    },
  },
};

const CARD_SX = {
  background: "#fff",
  border: "1px solid #e8e8e8",
  borderRadius: "12px",
  padding: "20px 24px",
  height: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
};

function formatMetricValue(value, suffix) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${Number(value)}${suffix}`;
}

function CustomDot(props) {
  const { cx, cy } = props;
  if (cx == null || cy == null) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill="#185fa5"
      stroke="#185fa5"
      strokeWidth={0}
    />
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        padding: "10px 12px",
        fontSize: 12,
        lineHeight: 1.5,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div style={{ color: COLORS.treatmentRate }}>
        {LABELS.treatmentRate}: {formatMetricValue(row.treatmentRate, "%")}
      </div>
      <div style={{ color: COLORS.percentagePending }}>
        {LABELS.percentagePending}:{" "}
        {formatMetricValue(row.percentagePending, "%")}
      </div>
      <div style={{ color: COLORS.timeToTherapy }}>
        {LABELS.timeToTherapy}: {formatMetricValue(row.timeToTherapy, " days")}
      </div>
    </div>
  );
}

function LegendSwatch({ type, label, description }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {type === "line" ? (
        <span
          style={{
            display: "inline-block",
            width: 18,
            height: 0,
            borderTop: `2.5px dashed ${COLORS.timeToTherapy}`,
          }}
        />
      ) : (
        <span
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: 2,
            backgroundColor:
              type === "treatmentRate"
                ? COLORS.treatmentRate
                : COLORS.percentagePending,
          }}
        />
      )}
      <span style={{ fontSize: 12, color: "#555" }}>{label}</span>
      {description ? (
        <MuiTooltip
          title={description}
          placement="top"
          slotProps={INFO_TOOLTIP_SLOT_PROPS}
        >
          <Box
            component="span"
            aria-label={`Info about ${label}`}
            sx={{
              lineHeight: 0,
              display: "inline-flex",
              cursor: "default",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14, color: "#A0A0A0" }} />
          </Box>
        </MuiTooltip>
      ) : null}
    </div>
  );
}

function WidgetHeader({ title, subtitle, onExplore }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 16,
        marginBottom: 12,
        flexWrap: "wrap",
        flexShrink: 0,
        flexDirection: "column",
      }}
    >
      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            width: "100%",
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 600, color: "#1a1a1a" }}>
            {title}
          </div>
          {onExplore ? (
            <Button
              variant="text"
              size="small"
              onClick={() => onExplore()}
              sx={{
                textTransform: "none",
                fontSize: 14,
                fontWeight: 600,
                color: "#D96B04",
                minWidth: "auto",
                p: 0,
                flexShrink: 0,
                marginRight: "30px",
              }}
            >
              Explore
            </Button>
          ) : null}
        </div>
        <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
          {subtitle}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <LegendSwatch
          type="treatmentRate"
          label={LABELS.treatmentRate}
          description={LABEL_DESCRIPTIONS.treatmentRate}
        />
        <LegendSwatch
          type="percentagePending"
          label={LABELS.percentagePending}
          description={LABEL_DESCRIPTIONS.percentagePending}
        />
        <LegendSwatch
          type="line"
          label={LABELS.timeToTherapy}
          description={LABEL_DESCRIPTIONS.timeToTherapy}
        />
      </div>
    </div>
  );
}

function ChartEmptyState({ message }) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 260,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#FFFFFF",
      }}
    >
      <Typography
        fontSize={12}
        sx={{ color: "#718096", textAlign: "center", px: 2 }}
      >
        {message}
      </Typography>
    </Box>
  );
}

export default function TreatmentRateWidget({
  data = [],
  title = TREATMENT_RATE_WIDGET_TEXT.title,
  subtitle = TREATMENT_RATE_WIDGET_TEXT.subtitle,
  partialMonthNote = null,
  isLoading = false,
  isError = false,
  emptyMessage = TREATMENT_RATE_WIDGET_TEXT.emptyMessage,
  errorMessage = TREATMENT_RATE_WIDGET_TEXT.errorMessage,
  onExplore,
}) {
  const chartData = Array.isArray(data) ? data : [];
  const hasData = chartData.length > 0;
  const footerNote = hasData ? partialMonthNote : null;

  if (isLoading) {
    return (
      <Box sx={CARD_SX}>
        <WidgetHeader title={title} subtitle={subtitle} />
        <Skeleton variant="rounded" height={260} sx={{ flex: 1 }} />
      </Box>
    );
  }
  console.log("onExplore", onExplore);
  return (
    <Box sx={CARD_SX}>
      <WidgetHeader title={title} subtitle={subtitle} onExplore={onExplore} />

      {isError ? (
        <ChartEmptyState message={errorMessage} />
      ) : !hasData ? (
        <ChartEmptyState message={emptyMessage} />
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              barCategoryGap="25%"
              barGap={3}
              margin={{ top: 8, right: 24, left: 16, bottom: 24 }}
            >
              <CartesianGrid vertical={false} stroke="rgba(0,0,0,0.07)" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10, fill: "#888" }}
                tickLine={false}
                axisLine={false}
                interval={0}
                angle={-35}
                textAnchor="end"
                height={55}
                label={{
                  value: TREATMENT_RATE_WIDGET_TEXT.referralMonthAxis,
                  position: "insideBottom",
                  offset: -10,
                  style: { fontSize: 11, fill: "#888", textAnchor: "middle" },
                }}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                domain={[0, 100]}
                tickCount={5}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 11, fill: "#aaa" }}
                tickLine={false}
                axisLine={false}
                width={72}
                label={{
                  value: TREATMENT_RATE_WIDGET_TEXT.leftAxis,
                  angle: -90,
                  position: "insideLeft",
                  offset: 10,
                  style: { fontSize: 11, fill: "#888", textAnchor: "middle" },
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 35]}
                tickCount={5}
                tickFormatter={(v) => `${v}d`}
                tick={{ fontSize: 11, fill: "#aaa" }}
                tickLine={false}
                axisLine={false}
                width={72}
                label={{
                  value: TREATMENT_RATE_WIDGET_TEXT.rightAxis,
                  angle: 90,
                  position: "insideRight",
                  offset: 10,
                  style: { fontSize: 11, fill: "#888", textAnchor: "middle" },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                yAxisId="left"
                dataKey="treatmentRate"
                fill={COLORS.treatmentRate}
                radius={[3, 3, 0, 0]}
                maxBarSize={18}
              />
              <Bar
                yAxisId="left"
                dataKey="percentagePending"
                fill={COLORS.percentagePending}
                radius={[3, 3, 0, 0]}
                maxBarSize={18}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="timeToTherapy"
                stroke={COLORS.timeToTherapy}
                strokeWidth={2.5}
                strokeDasharray="5 3"
                dot={<CustomDot />}
                activeDot={{
                  r: 5,
                  fill: "#185fa5",
                  stroke: "#fff",
                  strokeWidth: 2,
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}

      {footerNote ? (
        <Typography
          component="div"
          sx={{
            textAlign: "right",
            fontSize: 11,
            color: "#aaa",
            mt: 1,
            flexShrink: 0,
          }}
        >
          {footerNote}
        </Typography>
      ) : null}
    </Box>
  );
}
